import { useMutation } from '@tanstack/react-query';
import { cloneDeep } from 'lodash-es';
import React, { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FieldValues, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { v4 as uuid } from 'uuid';

import { Button, Currency, Dropdown, DropdownOption, Icon, Tooltip } from '@column/column-ui-kit';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { IconTransfers } from '~/elements';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { useOptimisticTransferAdd } from '~/lib/api';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import {
  AchRepository,
  AchTransfer,
  BookRepository,
  BookTransfer,
  CheckDepositRequest,
  CheckIssueRequest,
  CheckRepository,
  CheckTransfer,
  Counterparty,
  CreateAchTransfer,
  CreateBookTransfer,
  CreateIntlWireTransfer,
  CreateWireTransfer,
  IntlWireRepository,
  IntlWireTransfer,
  TransferEntryType,
  TransferEntryTypes,
  TransferTypeID,
  WireRepository,
  WireTransfer,
  transfers,
} from '~/repositories';
import { AchBatchRepository } from '~/repositories/Transfer/AchBatchRepository';
import {
  CreateRealtimeTransfer,
  RealtimeRepository,
  RealtimeTransfer,
} from '~/repositories/Transfer/RealtimeRepository';
import { TransferBatchRepository } from '~/repositories/Transfer/TransferBatchRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { EditToolbar, Inner, Line } from '~/styles';
import { CreateTransferBatchRequest } from '~/typings/API';
import { getDateFormat, scrollToOffset } from '~/util';
import { log } from '~/util/log';

import { ComingSoonBadge } from './_components/ComingSoonBadge';
import { ComponentLoader } from './_components/ComponentLoader';
import { RestrictedBadge } from './_components/RestrictedBadge';
import { mergeCheckDepositNumbers } from './_utils/check';
import { isReviewRequired } from './_utils/transferReview';
import type { AchBatchFormValues } from './TypeContent/ach-batch';
import {
  createCheckDepositRequest,
  detectDuplicateChecks,
  getTransferBatchDescription,
  groupChecksByBankAccount,
} from './TypeContent/canon-scanner/utils';

interface Params {
  type: TransferTypeID;
}

export interface LocationState {
  step?: 'index' | 'progress' | 'review';
  scanState?: 'complete';
  trigger?: 'checkScannerReset';
}

interface TransferTypeStatus {
  isActive: boolean;
  isDisabled: boolean;
  isRestricted: boolean;
}

interface CheckDepositAdditionalFields {
  accountNumber?: string;
  checkNumber?: string;
}

interface IntlWireAdditionalFields {
  counterparty?: Counterparty;
  defaultCurrency?: Currency;
}

interface CheckDepositScannerFields {
  itemId?: string;
  state?: 'success' | 'error';
  stateMessage?: string;
  imageFrontJPG?: string;
  imageBackJPG?: string;
}

type IdempotencyKey = { idempotencyKey?: string };

type CheckDeliveryMethod = 'usps_first_class' | 'fedex_two_day' | 'fedex_overnight';
type MailCheckRequest = NonNullable<CheckIssueRequest['mailCheckRequest']> & {
  deliveryMethod?: CheckDeliveryMethod;
};
type IssueCheckRequest = Omit<CheckIssueRequest, 'mailCheckRequest'> & {
  mailCheckRequest?: MailCheckRequest;
};
type IssueCheckAddress = NonNullable<MailCheckRequest['payorAddress']>;

const hasMailableAddressDetail = (address?: IssueCheckAddress) =>
  [address?.line1, address?.line2, address?.city, address?.state, address?.postalCode].some(
    (value) => value !== undefined && value !== null && String(value).trim() !== ''
  );

export type IntlWire = CreateIntlWireTransfer & IntlWireAdditionalFields & IdempotencyKey;
export type CreateCheckDeposit = CheckDepositRequest & CheckDepositAdditionalFields & IdempotencyKey;
export type CreateCheckScanner = CreateCheckDeposit;
export type CreateCheckDepositScanner = CreateCheckDeposit & CheckDepositScannerFields;

type TransferCreateResult =
  | AchTransfer
  | BookTransfer
  | WireTransfer
  | CheckTransfer
  | IntlWireTransfer
  | RealtimeTransfer;

export type CreateTransfer = {
  ach: CreateAchTransfer & IdempotencyKey;
  book: CreateBookTransfer & IdempotencyKey;
  wire: CreateWireTransfer & IdempotencyKey;
  issueCheck: IssueCheckRequest & IdempotencyKey;
  rdc: CreateCheckDeposit;
  intlWire: IntlWire;
  checkScanner: CreateCheckScanner;
  checkScannerType: CreateCheckDepositScanner[];
  realtime: CreateRealtimeTransfer & IdempotencyKey;
  achBatch: AchBatchFormValues;
};

const TRANSFER_ROUTES: Record<TransferTypeID, keyof CreateTransfer> = {
  ach: 'ach',
  book: 'book',
  wire: 'wire',
  'wire-drawdown': 'wire',
  'issue-check': 'issueCheck',
  rdc: 'rdc',
  check: 'checkScannerType',
  'intl-wire': 'intlWire',
  realtime: 'realtime',
  'ach-batch': 'achBatch',
};

const Form = styled.form`
  margin: 0;
  display: flex;
  flex-direction: column;
`;

const TransferTypeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TransferTypeList = styled.div`
  display: none;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: grid;
    grid-gap: 8px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const TransferTypeEntry = styled.div<Partial<TransferTypeStatus> & { $isCheckScanner?: boolean }>`
  padding: 12px 16px;
  display: grid;
  justify-items: start;
  grid-gap: 12px;
  cursor: pointer;
  position: relative;
  border-radius: 8px;
  background-color: transparent;
  transition:
    background-color 0.15s ease-out,
    box-shadow 0.15s ease-out;
  box-shadow:
    0 -1px 1px rgba(255, 255, 255, 0) inset,
    ${({ theme }) => theme.style.buttonSecondaryBorderProperties}
      ${({ theme }) => theme.style.buttonSecondaryBorderWidth} ${({ theme }) => theme.style.buttonSecondaryBorderColor},
    0 1px 1px rgba(0, 0, 0, 0.1),
    0 1px 4px 0 transparent inset;
  align-items: center;
  grid-template-columns: 1fr min-content;

  /* Hide Check Scanner below tablet breakpoint */
  ${({ $isCheckScanner }) =>
    $isCheckScanner &&
    css`
      @media (max-width: 767px) {
        display: none;
      }
    `}

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    align-items: flex-start;
  }

  ${({ isActive, isDisabled, isRestricted }) =>
    isActive
      ? css`
          background-color: ${({ theme }) =>
            theme.id !== 'dark' ? theme.background : theme.secondary.blendToBackground(25)};
          box-shadow:
            ${({ theme }) => theme.style.buttonSecondaryBorderProperties} 1.5px
              ${({ theme }) => theme.primary.background},
            0 0 0 var(--button-outline, 0px)
              var(--button-outline-color, ${({ theme }) => theme.style.buttonFocussedOutlineColor}),
            ${({ theme }) => theme.style.buttonSecondaryShadow};
        `
      : (isRestricted || isDisabled) &&
        css`
          cursor: not-allowed;
          background-color: ${({ theme }) =>
            theme.id !== 'dark' ? theme.secondary.blendToBackground(25) : theme.secondary.blendToBackground(-5)};
          box-shadow:
            0 -1px 1px rgba(255, 255, 255, 0) inset,
            ${({ theme }) => theme.style.buttonSecondaryBorderProperties}
              ${({ theme }) => theme.style.buttonSecondaryBorderWidth}
              ${({ theme }) => theme.style.buttonSecondaryBorderColor};
        `}

  ${({ isActive, isRestricted, isDisabled }) =>
    !isActive &&
    !isRestricted &&
    !isDisabled &&
    css`
      &:hover {
        background-color: ${({ theme }) =>
          theme.id !== 'dark' ? theme.secondary.blendToBackground(50) : theme.secondary.blendToBackground(25)};

        box-shadow:
          0 -1px 1px rgba(255, 255, 255, ${({ theme }) => (theme.id !== 'dark' ? '.75' : '.075')}) inset,
          ${({ theme }) => theme.style.buttonSecondaryHoveredBorderProperties}
            ${({ theme }) => theme.style.buttonSecondaryHoveredBorderWidth}
            ${({ theme }) => theme.secondary.blendToBackground(1100, 150)},
          0 1px 1px rgba(0, 0, 0, 0.1),
          0 1px 4px 0 ${({ theme }) => theme.secondary.blendToBackground(120)} inset;
      }
    `}
    ${({ isActive, isRestricted }) =>
    isActive &&
    isRestricted &&
    css`
      background-color: ${({ theme }) =>
        theme.id !== 'dark' ? theme.secondary.blendToBackground(25) : theme.secondary.blendToBackground(-5)};
      box-shadow: ${({ theme }) =>
        `${theme.style.buttonSecondaryBorderProperties} 1.5px ${theme.secondary.blendToBackground(750)}`};
    `}
`;

const TransferTypeLockup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const TransferTypeIcon = styled.div<Partial<TransferTypeStatus>>`
  --icon-size: 20px;

  transition: color 0.2s;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};

  ${({ isActive }) =>
    isActive &&
    css`
      color: ${({ theme }) => theme.primary.background};
    `}
`;

const TransferTypeLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

const TransferEntryTime = styled.div`
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  display: flex;
  gap: 4px;
  line-height: 14px;
  font-size: 11px;
  font-weight: 500;

  svg {
    --icon-size: 14px;
  }
`;

const ButtonRight = styled.div`
  display: flex;
  gap: 12px;
  margin-left: auto;
`;

const TransferTypeDropdownWrapper = styled.div`
  display: block;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const TransferTypeDropdownButton = styled.button<{ isActive?: boolean }>`
  width: 100%;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  border-radius: 8px;
  background-color: transparent;
  border: none;
  transition:
    background-color 0.15s ease-out,
    box-shadow 0.15s ease-out;
  box-shadow:
    0 -1px 1px rgba(255, 255, 255, 0) inset,
    ${({ theme }) => theme.style.buttonSecondaryBorderProperties}
      ${({ theme }) => theme.style.buttonSecondaryBorderWidth} ${({ theme }) => theme.style.buttonSecondaryBorderColor},
    0 1px 1px rgba(0, 0, 0, 0.1),
    0 1px 4px 0 transparent inset;

  &:hover {
    background-color: ${({ theme }) =>
      theme.id !== 'dark' ? theme.secondary.blendToBackground(50) : theme.secondary.blendToBackground(25)};
    box-shadow:
      0 -1px 1px rgba(255, 255, 255, ${({ theme }) => (theme.id !== 'dark' ? '.75' : '.075')}) inset,
      ${({ theme }) => theme.style.buttonSecondaryHoveredBorderProperties}
        ${({ theme }) => theme.style.buttonSecondaryHoveredBorderWidth}
        ${({ theme }) => theme.secondary.blendToBackground(1100, 150)},
      0 1px 1px rgba(0, 0, 0, 0.1),
      0 1px 4px 0 ${({ theme }) => theme.secondary.blendToBackground(120)} inset;
  }

  &:focus {
    outline: none;
  }
`;

const TransferTypeDropdownContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TransferTypeDropdownIcon = styled.div`
  --icon-size: 20px;
  color: ${({ theme }) => theme.primary.background};
  display: flex;
  align-items: center;
`;

const TransferTypeDropdownLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.secondary.background};
`;

const TransferTypeDropdownChevron = styled.div`
  --icon-size: 16px;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  display: flex;
  align-items: center;
`;

const StyledDropdown = styled(Dropdown)`
  width: 100%;

  --dropdown-entry-padding: 12px;
  --dropdown-max-height: 328px;

  button {
    padding: 9px 12px;
  }

  [role='menuitem'] {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
`;

const DropdownOptionSuffix = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const DropdownOptionIconWrapper = styled.div<{ $isActive: boolean }>`
  --icon-size: 20px;
  display: flex;
  align-items: center;
  color: ${({ theme, $isActive }) => ($isActive ? theme.primary.background : theme.secondary.blendToBackground(800))};
`;

export const PageTransfersAdd: FC = () => {
  const { addDangerNotification, addInfoNotification, addSuccessNotification } = useNotificationStore();
  const { currentPlatform, isAuthorized } = useSessionStore();
  const isSandbox = useSessionStore((sessionState) => sessionState.isSandbox);
  const navigate = useNavigate();
  const location = useLocation();
  const { type } = useParams<keyof Params>() as Params;
  const [createButtonLoading, setCreateButtonLoading] = useState<boolean>(false);
  const { handleFormErrors } = useFormErrorHandler();
  const optimisticallyAddTransfer = useOptimisticTransferAdd();

  const state = location.state as LocationState;

  const isPaniniCheckScannerEnabled = useFeatureFlag(FeatureFlag.EnablePaniniCheckScanner, { default: false });
  const hasInternationalWires = useFeatureFlag(FeatureFlag.HasInternationalWires, { default: true });
  const transferBatchesEnabled = useFeatureFlag(FeatureFlag.EnableTransferBatches);
  const achBatchEnabled = useFeatureFlag(FeatureFlag.EnableAchTransferBatches);
  const enableSwiftTransferApprovals = useFeatureFlag(FeatureFlag.EnableSwiftTransferApprovals);

  const showCreateButton = useMemo(
    () => !(type === 'ach-batch' && !achBatchEnabled) && (type !== 'check' || state?.step === 'review'),
    [type, state, achBatchEnabled]
  );
  const createButtonDisabled = useMemo(() => {
    const permissionsOnView = transfers.find((entry) => entry.id === type)?.permissionName;
    return (permissionsOnView ?? []).every(
      (permission) =>
        !isAuthorized({
          permission: permission,
          permissionLevel: 'write',
        })
    );
  }, [isAuthorized, transfers, type]);

  const batchByBankAccountIdRef = useRef<{ [bankAccountId: string]: string }>({});
  const batchesWithDepositsRef = useRef<Set<string>>(new Set());

  const { mutateAsync: createTransferBatches, reset: resetTransferBatches } = useMutation({
    mutationFn: async (requests: CreateTransferBatchRequest[]) => {
      const effectiveRequests = requests.filter((request) => !batchByBankAccountIdRef.current[request.bankAccountId]);
      const results = await Promise.allSettled(
        effectiveRequests.map((request) => TransferBatchRepository.create(request))
      );
      results
        .filter((result): result is PromiseFulfilledResult<any> => result.status === 'fulfilled')
        .forEach((batch) => {
          batchByBankAccountIdRef.current[batch.value.bankAccountId] = batch.value.id;
        });
      return batchByBankAccountIdRef.current;
    },
  });

  const { mutateAsync: deleteTransferBatches } = useMutation({
    mutationFn: async (batchIds: string[]) =>
      Promise.allSettled(batchIds.map((batchId) => TransferBatchRepository.delete(batchId).then(() => batchId))),
    onSuccess: (results) => {
      const successfullyDeletedBatchIds = results
        .filter((result): result is PromiseFulfilledResult<string> => result.status === 'fulfilled')
        .map((result) => result.value);
      successfullyDeletedBatchIds.forEach((batchId) => {
        batchByBankAccountIdRef.current = Object.fromEntries(
          Object.entries(batchByBankAccountIdRef.current).filter(([, value]) => value !== batchId)
        );
      });
    },
  });

  const methods = useForm<CreateTransfer>({
    mode: 'onChange',
    defaultValues: {
      ach: {
        idempotencyKey: uuid(),
      },
      wire: {
        idempotencyKey: uuid(),
      },
      book: {
        idempotencyKey: uuid(),
      },
      issueCheck: {
        idempotencyKey: uuid(),
      },
      rdc: {
        idempotencyKey: uuid(),
      },
      realtime: {
        idempotencyKey: uuid(),
      },
      achBatch: {
        idempotencyKey: uuid(),
      },
    },
  });
  const {
    handleSubmit,
    reset,
    resetField,
    trigger,
    formState: { errors: errorState },
    setValue,
    watch,
    clearErrors,
  } = methods;

  const handleCancelButton = useCallback(
    (buttonLeft?: boolean) => {
      if (state?.step === 'review' && buttonLeft) {
        navigate(`${ROUTE.TRANSFERS}/edit/check`, {
          state: {
            step: 'index',
            scanState: undefined,
            trigger: 'checkScannerReset',
          },
        });
        return;
      }
      if (type !== 'check' || (state?.step && state?.step === 'index')) {
        navigate(ROUTE.TRANSFERS);
        return;
      }
      navigate(-1);
    },
    [type, state]
  );

  const handleReviewClick = useCallback(async () => {
    const validate = await trigger('checkScannerType');
    const errors = errorState?.checkScannerType;

    if (!validate) {
      if (!errors || !Array.isArray(errors) || errors.length < 1) {
        return;
      }
      errors.map((checkErrors, index) => {
        handleFormErrors({ errors: checkErrors, display: `notifications-check-${index}` });
      });
      return;
    }

    navigate(location.pathname, {
      state: {
        step: 'review',
        scanState: undefined,
      },
      replace: true,
    });
  }, [location.pathname, type, errorState, handleFormErrors, trigger]);

  const handleTransferTypeClick = useCallback(
    (id: string) => {
      clearErrors();
      navigate(`${ROUTE.TRANSFERS}/edit/${id}`, { replace: true });
    },
    [clearErrors, navigate]
  );

  const createHandler = useCallback(
    <T extends TransferCreateResult>(
      promise: Promise<T>,
      transferType: TransferEntryType,
      getMessage?: (result: T) => { message: string; variant: 'info' | 'success' }
    ) => {
      promise
        .then((result) => {
          scrollToOffset(0);

          if (result?.id) {
            const rawAmount =
              ('amount' in result ? result.amount : undefined) ??
              ('depositedAmount' in result ? result.depositedAmount : undefined) ??
              ('positivePayAmount' in result ? result.positivePayAmount : undefined);
            const amount =
              typeof rawAmount === 'number' ? rawAmount : typeof rawAmount === 'string' ? Number(rawAmount) : undefined;

            if (amount === undefined || isNaN(amount)) {
              log({
                name: 'Transfer created without valid amount, skipping optimistic update',
                type: 'error',
                context: { result },
              });
            } else {
              const senderBankAccountId = 'senderBankAccountId' in result ? result.senderBankAccountId : undefined;
              const receiverBankAccountId =
                'receiverBankAccountId' in result ? result.receiverBankAccountId : undefined;
              const bankAccountId = 'bankAccountId' in result ? result.bankAccountId : undefined;

              optimisticallyAddTransfer({
                id: result.id,
                type: transferType,
                amount,
                currencyCode: result.currencyCode ?? 'USD',
                description: result.description ?? '',
                status: result.status,
                createdAt: new Date(),
                isIncoming: 'isIncoming' in result ? result.isIncoming : false,
                senderInternalAccount: senderBankAccountId
                  ? { bankAccountId: senderBankAccountId, accountNumberId: '' }
                  : undefined,
                receiverInternalAccount: receiverBankAccountId
                  ? { bankAccountId: receiverBankAccountId, accountNumberId: '' }
                  : bankAccountId
                    ? { bankAccountId: bankAccountId, accountNumberId: '' }
                    : undefined,
                checkNumber:
                  'checkNumber' in result && result.checkNumber !== null && result.checkNumber !== undefined
                    ? String(result.checkNumber)
                    : undefined,
              });
            }
          }

          navigate(ROUTE.TRANSFERS);

          if (getMessage) {
            const message = getMessage(result);
            if (message.variant === 'success') {
              addSuccessNotification({
                content: message.message,
              });
            } else {
              addInfoNotification({
                content: message.message,
              });
            }
          } else {
            addSuccessNotification({
              content: 'Transfer created',
            });
          }
        })
        .catch((error) => {
          addDangerNotification({
            content: error.message,
          });
        })
        .finally(() => {
          setCreateButtonLoading(false);
        });
    },
    [optimisticallyAddTransfer, navigate, addSuccessNotification, addInfoNotification, addDangerNotification]
  );

  const onSuccess = useCallback(
    (transfer: CreateTransfer) => {
      const data = cloneDeep<CreateTransfer>(transfer);

      if (createButtonLoading) {
        return;
      }

      setCreateButtonLoading(true);

      if (type === 'ach-batch') {
        const { bankAccountId, accountNumberId, description, statementGroupingMode, idempotencyKey } = data.achBatch;
        AchBatchRepository.create(
          {
            importType: 'nacha',
            importConfig: { nacha: { bankAccountId, accountNumberId: accountNumberId || undefined } },
            description: description || undefined,
            statementGroupingMode,
          },
          idempotencyKey
        )
          .then((batch) => {
            addSuccessNotification({ content: 'Batch created' });
            navigate(`${ROUTE.TRANSFERS}/batches/ach/${batch.id}`);
          })
          .catch((error) => {
            addDangerNotification({ content: error.message });
            setValue('achBatch.idempotencyKey', uuid());
          })
          .finally(() => setCreateButtonLoading(false));
        return;
      }

      if (type === 'ach') {
        data.ach.currencyCode = 'USD';

        if (!data.ach.effectiveDate || data.ach.effectiveDate.length < 1) {
          data.ach.effectiveDate = getDateFormat(new Date());
        }

        createHandler(
          AchRepository.create(data.ach),
          data.ach.type === 'CREDIT' ? TransferEntryTypes.AchCredit : TransferEntryTypes.AchDebit,
          ({ status }) =>
            status.toLowerCase() === 'pre_review'
              ? { message: 'Transfer submitted for approval', variant: 'info' }
              : { message: 'Transfer created', variant: 'success' }
        );

        return;
      }

      if (type === 'book') {
        data.book.currencyCode = 'USD';

        createHandler(BookRepository.create(data.book), TransferEntryTypes.Book);

        return;
      }

      if (type === 'wire') {
        data.wire.currencyCode = 'USD';

        createHandler(WireRepository.create(data.wire), TransferEntryTypes.Wire, ({ status }) =>
          status.toLowerCase() === 'pre_review'
            ? { message: 'Transfer submitted for approval', variant: 'info' }
            : { message: 'Transfer created', variant: 'success' }
        );

        return;
      }

      if (type === 'intl-wire') {
        delete data.intlWire.counterparty;
        delete data.intlWire.defaultCurrency;

        createHandler(IntlWireRepository.create(data.intlWire), TransferEntryTypes.Swift);

        return;
      }

      if (type === 'issue-check') {
        data.issueCheck.currencyCode = 'USD';

        const { idempotencyKey, ...issueCheckData } = data.issueCheck;

        if (issueCheckData.customizedCheckNumber !== undefined) {
          issueCheckData.customizedCheckNumber = Number(issueCheckData.customizedCheckNumber);
        }

        if (Object.values(issueCheckData.mailCheckRequest?.payeeAddress ?? {}).every((value) => value === undefined)) {
          delete issueCheckData.mailCheckRequest;
        } else if (
          issueCheckData.mailCheckRequest &&
          !hasMailableAddressDetail(issueCheckData.mailCheckRequest.payorAddress)
        ) {
          delete issueCheckData.mailCheckRequest.payorAddress;
        }
        createHandler(CheckRepository.issue(issueCheckData, idempotencyKey), TransferEntryTypes.CheckDebit);
        return;
      }

      if (type === 'rdc') {
        data.rdc.currencyCode = 'USD';

        const { idempotencyKey, ...rdcData } = data.rdc;

        createHandler(
          CheckRepository.deposit(mergeCheckDepositNumbers(rdcData), idempotencyKey),
          TransferEntryTypes.CheckCredit
        );
        return;
      }

      if (type === 'check') {
        const DuplicateCheckError = new Error('Duplicate checks detected');

        const submitCheckDeposit = async (
          checkData: CreateCheckDepositScanner,
          index: number
        ): Promise<{ success: boolean; result?: any; error?: any }> => {
          if (checkData.imageFront && checkData.imageBack) {
            const request = createCheckDepositRequest(checkData);

            return CheckRepository.deposit(request, checkData.idempotencyKey)
              .then((result) => {
                setValue(`checkScannerType.${index}.state`, 'success');
                return { success: true, result };
              })
              .catch((error) => {
                addDangerNotification({
                  content: `Check #${index + 1}: ${error.message}`,
                  display: 'global',
                });

                setValue(`checkScannerType.${index}.state`, 'error');
                return { success: false, error };
              });
          } else {
            addDangerNotification({
              content: `Check #${index + 1}: Missing image or data`,
              display: 'global',
            });

            setValue(`checkScannerType.${index}.state`, 'error');
            return Promise.resolve({ success: false, error: 'Missing image or data' });
          }
        };

        const submitCheckDeposits = async () => {
          const { uniqueChecks, duplicateItemIds } = detectDuplicateChecks(data.checkScannerType);
          data.checkScannerType.forEach((check) => {
            if (check.itemId !== undefined && duplicateItemIds.has(String(check.itemId))) {
              const itemIndex = data.checkScannerType.findIndex((item) => item.itemId === check.itemId);
              setValue(`checkScannerType.${itemIndex}.state`, 'error');
            }
          });
          if (duplicateItemIds.size > 0) {
            addDangerNotification({
              content: `${duplicateItemIds.size} ${duplicateItemIds.size > 1 ? 'checks were flagged as duplicates, please remove duplicates and try again.' : 'check was flagged as a duplicate, please remove the duplicate and try again.'}`,
              display: 'global',
            });
            return Promise.reject(DuplicateCheckError);
          }

          const checksByBankAccount = groupChecksByBankAccount(uniqueChecks);
          const batchIdsByBankAccount = transferBatchesEnabled
            ? await createTransferBatches(
                Object.entries(checksByBankAccount)
                  .filter(([, checks]) => checks.length > 1)
                  .map(([bankAccountId]) => ({
                    description: getTransferBatchDescription(),
                    bankAccountId,
                  }))
              )
            : {};

          const pendingChecks = uniqueChecks.filter((e) => e.state !== 'success');
          const requests = pendingChecks.map((checkData) => {
            const itemIndex = data.checkScannerType.findIndex((item) => item.itemId === checkData.itemId);
            const transferBatchId =
              transferBatchesEnabled && checkData.bankAccountId
                ? batchIdsByBankAccount[checkData.bankAccountId]
                : undefined;
            return submitCheckDeposit({ ...checkData, transferBatchId }, itemIndex);
          });

          return Promise.all(requests);
        };

        submitCheckDeposits()
          .then((results) => {
            const allSuccess = results.every((result) => result.success);
            const successfulChecks = results.filter((result) => result.success);
            successfulChecks.forEach((check) => {
              if (check.result?.transferBatchId) {
                batchesWithDepositsRef.current.add(check.result.transferBatchId);
              }
              const depositedAmount =
                typeof check.result?.depositedAmount === 'number'
                  ? check.result.depositedAmount
                  : typeof check.result?.depositedAmount === 'string'
                    ? Number(check.result.depositedAmount)
                    : undefined;
              if (check.result?.id && depositedAmount !== undefined && !isNaN(depositedAmount)) {
                optimisticallyAddTransfer({
                  id: check.result.id,
                  type: TransferEntryTypes.CheckCredit,
                  amount: depositedAmount,
                  currencyCode: check.result.currencyCode ?? 'USD',
                  description: check.result.description ?? '',
                  status: check.result.status ?? 'pending_deposit',
                  createdAt: check.result.createdAt instanceof Date ? check.result.createdAt : new Date(),
                  isIncoming: true,
                  checkNumber: check.result.checkNumber ?? undefined,
                });
              }
            });

            if (successfulChecks.length > 0) {
              addSuccessNotification({
                content:
                  successfulChecks.length === 1
                    ? 'Check transfer added'
                    : `${successfulChecks.length} Check transfers added`,
                display: 'global',
              });
            }

            if (!allSuccess) {
              const emptyBatchIds = Object.values(batchByBankAccountIdRef.current).filter(
                (batchId) => !batchesWithDepositsRef.current.has(batchId)
              );
              if (emptyBatchIds.length > 0) {
                deleteTransferBatches(emptyBatchIds);
              }
            }

            if (allSuccess) {
              navigate(ROUTE.TRANSFERS);
              resetTransferBatches();
              batchByBankAccountIdRef.current = {};
              batchesWithDepositsRef.current = new Set();
            }
          })
          .catch((error) => {
            if (error === DuplicateCheckError) {
              return;
            }
            console.error('An unexpected error occurred', error);
          })
          .finally(() => {
            setCreateButtonLoading(false);
          });
      }

      if (type === 'realtime') {
        data.realtime.currencyCode = 'USD';
        const { idempotencyKey, ...realtimeData } = data.realtime;

        createHandler(RealtimeRepository.create(realtimeData, idempotencyKey), TransferEntryTypes.Realtime);
        return;
      }
    },
    [type, isSandbox, createButtonLoading]
  );

  const onError: SubmitHandler<FieldValues> = useCallback(
    (errors) => {
      const id = TRANSFER_ROUTES[type];
      const transferErrors = id ? errors[id] : undefined;
      const topLevelErrorEntries = transferErrors ? Object.entries(transferErrors) : [];

      if (!id || typeof transferErrors === 'undefined' || topLevelErrorEntries.length === 0) {
        return;
      }

      if (
        topLevelErrorEntries.length === 1 &&
        (type !== 'issue-check' || topLevelErrorEntries[0][0] !== 'mailCheckRequest')
      ) {
        return;
      }

      handleFormErrors({ errors: transferErrors });
    },
    [type, handleFormErrors]
  );

  useEffect(() => {
    if (state?.trigger === 'checkScannerReset') {
      resetField('checkScannerType', {
        defaultValue: [],
      });
      reset({
        checkScanner: {
          bankAccountId: undefined,
        },
      });
      resetTransferBatches();
      batchByBankAccountIdRef.current = {};
      batchesWithDepositsRef.current = new Set();

      navigate(location.pathname, { replace: true });
    }
  }, [state, resetField, location.pathname, resetTransferBatches]);

  useLayoutEffect(() => {
    if (!Object.keys(TRANSFER_ROUTES).includes(type)) {
      navigate(`${ROUTE.TRANSFERS}/edit/${TRANSFER_ROUTES.ach}`);
    }
  }, [type]);

  const renderTransfers = useMemo(() => {
    return transfers
      .filter((entry) => entry.id !== 'ach-batch' || achBatchEnabled)
      .map((entry) => {
        if (entry.id === 'intl-wire') {
          entry.isComingSoon = !hasInternationalWires;
        }
        return entry;
      });
  }, [hasInternationalWires, type, achBatchEnabled]);

  const achType = watch('ach.type');
  const subType = useMemo(() => {
    if (type !== 'ach') {
      return type;
    }

    if (achType === 'CREDIT') {
      return 'ach_credit';
    }

    return 'ach_debit';
  }, [achType, type]);

  const createButtonLabel = useMemo(() => {
    if (type === 'ach-batch') return 'Continue to Upload';
    if (isReviewRequired(subType, currentPlatform?.transferApprovalConfig, enableSwiftTransferApprovals)) {
      return 'Submit Transfer for Approval';
    }
    return 'Create Transfer';
  }, [type, subType, currentPlatform?.transferApprovalConfig, enableSwiftTransferApprovals]);

  const isTransferTemplatesEnabled = isAuthorized({ permission: 'transferTemplates', permissionLevel: 'read' });

  const selectedTransfer = useMemo(() => {
    return renderTransfers.find((entry) => entry.id === type);
  }, [renderTransfers, type]);

  const dropdownOptions = useMemo<DropdownOption[]>(() => {
    return renderTransfers
      .filter((entry) => {
        // Hide Check Scanner in dropdown on mobile/tablet breakpoints
        if (entry.id === 'check') {
          return false;
        }
        return true;
      })
      .map((entry) => {
        const isRestricted = (entry.permissionName ?? []).every(
          (permission) =>
            !isAuthorized({
              permission: permission,
              permissionLevel: 'write',
            })
        );
        const IconElement = IconTransfers[entry.icon];

        return {
          value: entry.id,
          label: entry.label,
          disabled: entry.isComingSoon || isRestricted,
          icon: (
            <DropdownOptionIconWrapper $isActive={type === entry.id}>
              <IconElement />
            </DropdownOptionIconWrapper>
          ),
          suffix: (
            <DropdownOptionSuffix>
              {entry.isInstant && !isRestricted && (
                <TransferEntryTime>
                  Instant
                  <IconTransfers.Clock />
                </TransferEntryTime>
              )}
              {entry.isComingSoon && <ComingSoonBadge />}
              {isRestricted && !entry.isComingSoon && (
                <RestrictedBadge content="You do not have permission to use this transfer type." />
              )}
            </DropdownOptionSuffix>
          ),
        };
      });
  }, [renderTransfers, isAuthorized, type, hasInternationalWires]);

  return (
    <FormProvider {...methods}>
      <Form onSubmit={handleSubmit(onSuccess, onError)}>
        <PageActions>
          <Button
            size="small"
            variant="muted"
            type="button"
            onClick={() => navigate(`${ROUTE.TRANSFERS}/templates`)}
            isDisabled={!isTransferTemplatesEnabled}
          >
            <Tooltip
              content="You do not have permission to use transfer templates."
              isOpen={isTransferTemplatesEnabled ? false : undefined}
              placement="left"
            >
              Transfer Templates
            </Tooltip>
          </Button>

          {state?.step === 'index' && (
            <Button onClick={() => handleCancelButton()} variant="secondary" size="small" type="button">
              Cancel
            </Button>
          )}
          {state?.step === 'review' && (
            <Button onClick={() => handleCancelButton(true)} variant="secondary" size="small" type="button">
              Cancel
            </Button>
          )}
          {showCreateButton && (
            <Button
              type="button"
              onClick={handleSubmit(onSuccess, onError)}
              isLoading={createButtonLoading}
              isDisabled={createButtonDisabled}
              size="small"
              icon={<Icon.Plus />}
            >
              {createButtonLabel}
            </Button>
          )}
          {state?.step === 'progress' && state?.scanState === 'complete' && (
            <Button onClick={handleReviewClick} type="button" size="small">
              Review
            </Button>
          )}
        </PageActions>

        <Inner pt={2} px="var(--page-margin)">
          <TransferTypeContainer>
            <TransferTypeDropdownWrapper>
              <StyledDropdown
                fullWidth
                active={type}
                options={dropdownOptions}
                onChange={(value) => {
                  if (value && value !== type) {
                    handleTransferTypeClick(value);
                  }
                }}
                maxHeight="auto"
                customLabel={
                  <TransferTypeDropdownButton type="button">
                    <TransferTypeDropdownContent>
                      {selectedTransfer && (
                        <>
                          <TransferTypeDropdownIcon>
                            {React.createElement(IconTransfers[selectedTransfer.icon])}
                          </TransferTypeDropdownIcon>
                          <TransferTypeDropdownLabel>{selectedTransfer.label}</TransferTypeDropdownLabel>
                        </>
                      )}
                    </TransferTypeDropdownContent>
                    <TransferTypeDropdownChevron>
                      <Icon.ChevronDown />
                    </TransferTypeDropdownChevron>
                  </TransferTypeDropdownButton>
                }
              />
            </TransferTypeDropdownWrapper>

            <TransferTypeList>
              {renderTransfers.map((entry) => {
                const isRestricted = (entry.permissionName ?? []).every(
                  (permission) =>
                    !isAuthorized({
                      permission: permission,
                      permissionLevel: 'write',
                    })
                );
                const IconElement = IconTransfers[entry.icon];

                return (
                  <TransferTypeEntry
                    key={entry.id === 'intl-wire' ? (hasInternationalWires ? 'has-intl-wire' : 'intl-wire') : entry.id}
                    isActive={type === entry.id}
                    isRestricted={isRestricted}
                    isDisabled={entry.isComingSoon}
                    $isCheckScanner={entry.id === 'check'}
                    onClick={() => !entry.isComingSoon && !isRestricted && handleTransferTypeClick(entry.id)}
                  >
                    <TransferTypeLockup>
                      <TransferTypeIcon>
                        <IconElement />
                      </TransferTypeIcon>
                      <TransferTypeLabel title={entry.label}>{entry.label}</TransferTypeLabel>
                    </TransferTypeLockup>
                    {entry.isInstant && !isRestricted && (
                      <TransferEntryTime>
                        Instant
                        <IconTransfers.Clock />
                      </TransferEntryTime>
                    )}
                    {entry.isComingSoon && <ComingSoonBadge />}
                    {isRestricted && !entry.isComingSoon && (
                      <RestrictedBadge content="You do not have permission to use this transfer type." />
                    )}
                  </TransferTypeEntry>
                );
              })}
            </TransferTypeList>
          </TransferTypeContainer>
        </Inner>

        <Line />

        <ComponentLoader path={Object.keys(TRANSFER_ROUTES).includes(type) ? type : 'ach'} />

        {!(type === 'check' && isPaniniCheckScannerEnabled) && (
          <Inner>
            <EditToolbar>
              {state?.step === 'index' && (
                <Button onClick={() => handleCancelButton()} variant="secondary" size="small" type="button">
                  Cancel
                </Button>
              )}
              <ButtonRight>
                {state?.step === 'review' && (
                  <Button onClick={() => handleCancelButton(true)} variant="secondary" size="small" type="button">
                    Cancel
                  </Button>
                )}
                {showCreateButton && (
                  <Button
                    isLoading={createButtonLoading}
                    isDisabled={createButtonDisabled}
                    size="small"
                    icon={<Icon.Plus />}
                  >
                    {createButtonLabel}
                  </Button>
                )}
                {state?.step === 'progress' && state?.scanState === 'complete' && (
                  <Button onClick={handleReviewClick} type="button" size="small">
                    Review
                  </Button>
                )}
              </ButtonRight>
            </EditToolbar>
          </Inner>
        )}
      </Form>
    </FormProvider>
  );
};
